import ApiRoutes from '@common/defs/api-routes';
import { Id } from '@common/defs/types';
import useApi, { FetchApiOptions } from '@common/hooks/useApi';
import { RawData } from '@modules/admin/defs/types';
import useAuth from '@modules/auth/hooks/api/useAuth';
import { USER_LEAD_STATUS } from '@modules/leads/defs/types';
import { User } from '@modules/users/defs/types';
import { safeLocalStorage } from '@common/utils/storage';

export interface LoginAsRequest {
  id: Id;
}
export interface BanTherapistRequest {
  email?: string;
  firstname?: string;
  lastname?: string;
  ban_reason: string;
}
export interface StatsExternalTherapists {
  total: number;
  contacted: number;
  registered: number;
  registrationCompleted: number;
  onboardingCompleted: number;
  calendarConnected: number;
  perRegion: Record<
    string,
    {
      total: number;
      contacted: number;
      registered: number;
      registrationCompleted: number;
      onboardingCompleted: number;
      calendarConnected: number;
    }
  >;
}
export interface TherapistSearchEntry {
  id: number;
  input: { zip: string };
  output: {
    status: 'success' | 'failure';
    reason: { code: string; details?: Record<string, unknown> } | null;
    result: {
      therapistUserId?: number;
      birdDistanceInKilometers?: number;
      drivingDistanceInKms?: number;
      drivingDurationInMins?: number;
      score?: { total: number };
      availableSlots?: { day: number; date: string; count: number }[];
      zip?: string;
    } | null;
    hasNearbyTherapist: boolean;
  };
  createdAt: string;
  latitude: number;
  longitude: number;
}

export interface TherapistSearchesData {
  searches: TherapistSearchEntry[];
  regionZipCodes: string[];
  regionCode: string;
}

export interface StatsLeads {
  users: Record<
    number,
    {
      userId: Id;
      total: number;
      perStatus: Partial<Record<USER_LEAD_STATUS, number>>;
    }
  >;
  perUser: Partial<
    Record<
      USER_LEAD_STATUS,
      {
        average: number | null;
        standardDeviation: number;
        coefficientOfVariation: number;
        percentiles: Record<string, number>;
      }
    >
  >;
  perLead: {
    total: number;
    claimed: number;
    unclaimed: number;
    phoneRevealed: number;
    averageClaimTimeMinutes: number;
    averageReachableTherapists: number;
    reachabilityPercentiles: Record<string, number>;
  };
}
const useAdmin = () => {
  const { mutate } = useAuth();
  const fetchApi = useApi();
  const apiRoutes = ApiRoutes.Admin;

  const loginAs = async ({ id }: LoginAsRequest, options?: FetchApiOptions) => {
    const url = apiRoutes.LoginAs.replace('{id}', id.toString());
    const response = await fetchApi<{ token: string; user?: User }>(url, {
      method: 'GET',
      ...options,
    });

    if (response.success && response.data?.token) {
      // Sauvegarder le token admin actuel avant de changer de compte
      const currentToken = safeLocalStorage.getItem('authToken');
      if (currentToken) {
        safeLocalStorage.setItem('previousAuthToken', currentToken);
      }
      // Sauvegarder la page courante pour y revenir après restore
      if (typeof window !== 'undefined') {
        safeLocalStorage.setItem(
          'adminReturnPath',
          window.location.pathname + window.location.search
        );
      }
      safeLocalStorage.setItem('authToken', response.data.token);
      await mutate?.();
    }
    return response;
  };

  const restoreAdminAccount = async () => {
    const previousToken = safeLocalStorage.getItem('previousAuthToken');
    if (previousToken) {
      safeLocalStorage.setItem('authToken', previousToken);
      safeLocalStorage.removeItem('previousAuthToken');
      await mutate?.();
      return true;
    }
    return false;
  };
  const readStatsLeads = async (options?: FetchApiOptions) => {
    const url = apiRoutes.ReadStatsLeads;
    const response = await fetchApi<StatsLeads>(url, {
      method: 'GET',
      ...options,
    });
    return response;
  };
  const readStatsExternalTherapists = async (options?: FetchApiOptions) => {
    const url = apiRoutes.ReadStatsExternalTherapists;
    const response = await fetchApi<StatsExternalTherapists>(url, {
      method: 'GET',
      ...options,
    });
    return response;
  };

  const readRawData = async (options?: FetchApiOptions) => {
    const url = apiRoutes.ReadRawData;
    const response = await fetchApi<RawData>(url, {
      method: 'GET',
      ...options,
    });
    response.data?.users.forEach((user) => {
      user.createdAt = new Date(user.createdAt);
      user.updatedAt = new Date(user.updatedAt);
      user.deletedAt = user.deletedAt ? new Date(user.deletedAt) : null;
      user.lastLoginAt = new Date(user.lastLoginAt);
      user.emailVerifiedAt = user.emailVerifiedAt ? new Date(user.emailVerifiedAt) : null;
      user.onboardingStartedAt = new Date(user.onboardingStartedAt);
      user.onboardingCompletedAt = user.onboardingCompletedAt
        ? new Date(user.onboardingCompletedAt)
        : null;
      // string to number lat/long
      user.latitude = Number(user.latitude);
      user.longitude = Number(user.longitude);
    });
    response.data?.contacts.forEach((contact) => {
      contact.metadata = contact.metadata
        ? JSON.parse(contact.metadata as unknown as string)
        : null;
      contact.createdAt = new Date(contact.createdAt);
      contact.updatedAt = new Date(contact.updatedAt);
      // string to number lat/long (vient du zip du contact)
      contact.latitude = Number(contact.latitude);
      contact.longitude = Number(contact.longitude);
    });
    response.data?.bookings.forEach((booking) => {
      booking.startsAt = new Date(booking.startsAt);
      booking.endsAt = new Date(booking.endsAt);
      booking.createdAt = new Date(booking.createdAt);
      booking.updatedAt = new Date(booking.updatedAt);
    });
    response.data?.leads.forEach((lead) => {
      // claimedAt peut être null si le lead n'a pas été revendiqué
      lead.claimedAt = lead.claimedAt ? new Date(lead.claimedAt) : null;
      lead.createdAt = new Date(lead.createdAt);
      lead.updatedAt = new Date(lead.updatedAt);
    });
    return response;
  };
  const getRegions = async (options?: FetchApiOptions) => {
    const url = apiRoutes.GetRegions;
    const response = await fetchApi<{ id: number; code: string; name: string }[]>(url, {
      method: 'GET',
      ...options,
    });
    return response;
  };

  const getTherapistSearches = async (regionId: number, options?: FetchApiOptions) => {
    const url = `${apiRoutes.GetTherapistSearches}?region_id=${regionId}`;
    const response = await fetchApi<TherapistSearchesData>(url, {
      method: 'GET',
      ...options,
    });
    return response;
  };

  const banTherapist = async (data: BanTherapistRequest, options?: FetchApiOptions) => {
    const url = apiRoutes.BanTherapist;
    return fetchApi(url, {
      method: 'POST',
      data,
      displaySuccess: true,
      ...options,
    });
  };

  return {
    loginAs,
    restoreAdminAccount,
    readStatsLeads,
    readStatsExternalTherapists,
    readRawData,
    getRegions,
    getTherapistSearches,
    banTherapist,
  };
};

export default useAdmin;
